import React, { Component } from 'react';
import PropTypes from 'prop-types'
import * as types from '../../../constants/actionTypes';
import { MAX_FILE_SIZE } from '../../../constants/constants';
import Drawer from '../../Common/Drawer/Drawer';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../../Common/Notification';
import Icon from '../../Common/Icon';
import InputText from '../../Common/InputText';
import TextArea from '../../Common/TextArea';
import MultiSelectBox from '../../Common/MultiSelectBox/MultiSelectBox';
import Toggle from '../../Common/Toggle/Toggle';
import './BaselineFileDrawer.scss'
import DropZone from 'react-dropzone';

export class BaselineFileDrawer extends Component {

    constructor(props, context) {
        super(props, context);

        const file = {
            id: 0,
            name: '',
            description: '',
            departmentId: '',
            departmentName: '',
            projectId: '',
            projectName: '',
            createdByDate: '',
            storageName: '',
            isActive: true,
            file: {}
        }

        this.state = {
            isFormValid: true,
            isLoading: false,
            file: file,
            isProjectEnabled: false,
            departments: [],
            projects: [],
            userWantsToOverwriteFile: false,
            isDropZoneActive: false,
            shouldRenderDropZone: false
        };
    }

    componentDidMount = async () => {
        try {
            this.setState({ isLoading: true });
            await Promise.all([this.loadFilters(), this.loadFile()]);

            // ✅ Delay rendering DropZone one tick after mount
            setTimeout(() => {
                this.setState({ shouldRenderDropZone: true });
            }, 0);
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    componentDidUpdate = (prevProps) => {
        if (this.props.fileId && (this.props.fileId !== prevProps.fileId)) {
            this.loadFile();
        }
    }

    onFormChange = (name, value) => {
        this.setState({ file: { ...this.state.file, [name]: value } });
    }

    onCancel = () => {
        this.setState({ isFormValid: true });
        this.props.onCancel();
    }

    loadFile = async () => {
        try {
            if (this.props.fileId && this.props.fileId !== "0") {
                const response = await this.props.filesActions.loadFile(this.props.fileId);
                if (response.type === types.LOAD_FILE_SUCCESS) {
                    let file = response.payload.customerFile;
                    let deptId = ''
                    if (file.departmentId) {
                        deptId = file.departmentId.toString();
                    }

                    let projId = ''
                    if (file.projectId) {
                        projId = file.projectId.toString();
                    }

                    this.setState({
                        file: {
                            ...file,
                            departmentId: deptId,
                            projectId: projId,
                            description: (file.description) ? file.description : ""
                        }
                    });
                } else if (response.type === types.LOAD_FILE_FAILURE) {
                    Notification("error", "Error", response.payload.message);
                }
            } else if (this.props.droppedFile) {
                // If a file was dropped to open this drawer, then automatically
                // set that file as the file to save
                this.setState({
                    file: {
                        ...this.state.file,
                        name: this.props.droppedFile.name,
                        file: this.props.droppedFile
                    }
                })
            }
        } catch (error) {
            Notification("error", "Error", error);
        }
    }

    loadFilters = async () => {
        try {
            const response = await this.props.filesActions.loadFilesFilters(this.props.subscriptionId);
            if (response.type === types.LOAD_FILES_FILTERS_SUCCESS) {
                this.setState({
                    isProjectEnabled: response.payload.isProjectEnabled,
                    departments: response.payload.departments,
                    projects: response.payload.projects
                });
            } else if (response.type === types.LOAD_FILES_FILTERS_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        }
    }

    validateForm = () => {
        const isProjectEnabled = this.state.isProjectEnabled;
        const { id, name, projectId, departmentId, file } = this.state.file;

        const isNewFile = id === 0;
        const isMissingFile = !(file && file.name && file.name.trim().length > 0 && file.size > 0);
        const isFileValid = this.props.isMobile ||
            (!this.props.isMobile && isNewFile && !isMissingFile) ||
            (!this.props.isMobile && !isNewFile);

        if (file && file.size > MAX_FILE_SIZE) {
            Notification("error", "", "Selected file cannot exceed 100MB in size");
            return false;
        }

        if (file && file.name && file.name.length > 50) {
            Notification("error", "", "Selected file name cannot exceed 50 characters");
            return false;
        }

        const isFormValid = (
            (name && name.trim().length > 0)
            &&
            (
                (!isProjectEnabled && departmentId && departmentId.trim().length > 0)
                ||
                (isProjectEnabled && projectId && projectId.trim().length > 0)
            )
            // File is required on a new upload, but you can update a file
            // entry without changing the file.  File is NOT required if isMobile,
            // because we do not allow uploads via mobile.
            && (isFileValid)
        );
        return isFormValid;
    }

    onSave = async () => {
        if (!this.validateForm()) {
            this.setState({ isFormValid: false });
            return;
        }

        try {
            this.setState({ isLoading: true });
            const response = await this.props.filesActions.saveFile(this.state.file, true);
            if (response.type === types.CREATE_FILE_SUCCESS || response.type === types.UPDATE_FILE_SUCCESS) {
                Notification("success", "Success", "File saved successfully");
                this.props.onComplete();
            } else if (response.type === types.CREATE_FILE_FAILURE || response.type === types.UPDATE_FILE_FAILURE) {
                Notification("error", "Error", response.payload.message);
                this.setState({ isLoading: false });
            }
        } catch (error) {
            Notification("error", "Error", error);
            this.setState({ isLoading: false });
        }
    }

    onChange = (e) => {
        this.onFormChange(e.target.name, e.target.value);
    }

    onToggleChange = (name, value) => {
        this.onFormChange(name, value);
    }

    onMultiSelectChange = (value, name) => {
        this.onFormChange(name, value.toString());
    }

    onFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            this.onFormChange("file", file);
        }
    }

    onDrop = (droppedFiles) => {
        const droppedFile = (droppedFiles && droppedFiles.length > 0) ? droppedFiles[0] : null;

        if (!droppedFile) {
            return;
        }

        // this.onFormChange("file", droppedFile);
        // this.setState({ isDropZoneActive: false });
        this.setState({
            file: {
                ...this.state.file,
                name: droppedFile.name,
                file: droppedFile
            },
            isDropZoneActive: false
        })
    }

    onDragEnter = () => {
        this.setState({ isDropZoneActive: true });
    }

    onDragLeave = () => {
        this.setState({ isDropZoneActive: false });
    }

    downloadFile = async () => {
        try {
            this.setState({ isLoading: true });
            await this.props.filesActions.download(this.state.file.storageName);
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    onOverwriteClick = () => {
        this.setState({ userWantsToOverwriteFile: true });
    }

    render() {
        const className = this.state.isFormValid ? "subscription-file-upload-form" : "subscription-file-upload-form was-validated";
        const isFileRequired = this.state.file.id === 0 && !this.props.isMobile;

        const showDownloadLink = this.state.file.id > 0;

        const showAnyFileUploadOptions = !this.props.isMobile;
        const showDroppedFileIndicator = showAnyFileUploadOptions && this.props.droppedFile;
        const showOverwriteButton = showAnyFileUploadOptions && showDownloadLink && !this.state.userWantsToOverwriteFile;
        /**
         * Show the file input if user is adding a new file or if they have 
         * indicated they want to overwrite an existing file
         */
        const showFileInput =
            showAnyFileUploadOptions &&
            this.state.shouldRenderDropZone &&
            (
                (showDownloadLink && this.state.userWantsToOverwriteFile) ||
                (!showDownloadLink)
            );

        return (
            <Drawer isWide={false} title="Upload File"
                onSaveClick={this.onSave}
                onCancelClick={this.onCancel}
            >
                <LoadingIndicator isLoading={this.state.isLoading}>
                    <form className={className} noValidate>
                        <div className="row">
                            <div className="col">
                                <InputText
                                    name="name"
                                    label="Name"
                                    placeholder="Name"
                                    type="text"
                                    value={this.state.file.name}
                                    onChange={this.onChange}
                                    isRequired={true}
                                    errorMessage="Name is required"
                                    maxLength={50}
                                />

                                <TextArea
                                    rows={2}
                                    name="description"
                                    label="Description"
                                    placeholder="Description"
                                    value={this.state.file.description}
                                    onChange={this.onChange}
                                    maxLength={500}
                                />

                                <MultiSelectBox name="departmentId" label="Department" idField="id" valueField="name"
                                    options={this.state.departments}
                                    defaultValue="Select a Department"
                                    selectedValues={this.state.file.departmentId.toString()}
                                    onChange={this.onMultiSelectChange}
                                    isRequired={!this.state.isProjectEnabled}
                                    errorMessage="Department is required"
                                    isMulti={false}
                                />
                                <Toggle name="isActive" text="Active" checked={this.state.file.isActive} onChange={this.onToggleChange} />
                                {this.state.isProjectEnabled && <MultiSelectBox name="projectId" label="Project" idField="id" valueField="name"
                                    options={this.state.projects}
                                    defaultValue="Select a Project"
                                    selectedValues={this.state.file.projectId.toString()}
                                    onChange={this.onMultiSelectChange}
                                    isRequired={this.state.isProjectEnabled}
                                    errorMessage="Project is required"
                                    isMulti={false}
                                />}

                                {showOverwriteButton && <button className="overwrite-file-button" onClick={this.onOverwriteClick}>Overwrite Existing File</button>}

                                {showFileInput &&
                                    <DropZone
                                        multiple={false}
                                        onDrop={this.onDrop}
                                        onDragEnter={this.onDragEnter}
                                        onDragLeave={this.onDragLeave}
                                        noClick
                                        noKeyboard
                                    >
                                        {({ getRootProps, getInputProps, open, isDragActive }) => (
                                            <div
                                                {...getRootProps({
                                                    className: `dropzone ${isDragActive ? 'active' : ''}`,
                                                    onClick: (e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        open(); // 👈 ONLY trigger file dialog here
                                                    }
                                                })}
                                            >
                                                <input
                                                    {...getInputProps()}
                                                    tabIndex={-1} // ensures browser doesn’t auto-focus
                                                    style={{ display: 'none' }} // hidden input, not focusable
                                                />
                                                {isDragActive ? (
                                                    <div className="dropzone-overlay">
                                                        <div className="overlay-content">
                                                            <Icon name="cloud-upload-alt" />
                                                            <p>Drop the file here...</p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="box text-center dropped-file">
                                                        <Icon name="cloud-upload-alt" />
                                                        <p>
                                                            <strong>Choose a file</strong> or drag it here
                                                        </p>
                                                        {this.state.file?.name && <p>{this.state.file.name}</p>}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </DropZone>

                                }
                                {showDownloadLink && <span className="download-link" onClick={this.downloadFile}><Icon name="download" />Download</span>}
                            </div>
                        </div>
                    </form>
                </LoadingIndicator>
            </Drawer>
        )
    }
}

BaselineFileDrawer.propTypes = {
    subscriptionId: PropTypes.string.isRequired,
    fileId: PropTypes.string.isRequired,
    onComplete: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    filesActions: PropTypes.object.isRequired,
    isMobile: PropTypes.bool.isRequired,
    droppedFile: PropTypes.object
};

export default BaselineFileDrawer;